import { Link, useNavigate } from "react-router-dom";
import NoData from "../../assets/img/nodata_green.svg";
import { useAppDispatch, useAppSelector } from "../../app/hooks";

import {
  TypeEventsTicketItem,
  fetchEventsTicketItems,
  removeEventsTicketItem,
  resetCursor,
  selectEventsTicketItems,
  selectEventsTicketItemsHasMore,
} from "../../features/eventsticketitems/eventsTicketItemsSlice";
import { useEffect, useState } from "react";
import { formatDate } from "../../helpers/commons";
import {
  selectNtumaAccount,
  selectAccessToken,
} from "../../features/auth/authSlice";
import AppSpinner from "../elements/AppSpinner";
import { APP_STATUS } from "../../helpers/constants";
import { showToastMessage } from "../../features/generalSlice";
import { toast } from "react-toastify";
import useApiRequest from "../../helpers/api_request";
import { SHORTEN_URL, BASE_URL } from "../../helpers/constants";
import { AxiosError } from "axios";
import EventDetailsModal from "./EventDetailsModal";

const EventsTicketLinksPage = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const eventsTicketItems = useAppSelector(selectEventsTicketItems);
  const hasMore = useAppSelector(selectEventsTicketItemsHasMore);
  const ntumaAccount = useAppSelector(selectNtumaAccount);
  const loadingLinksStatus = useAppSelector(
    (state) => state.eventsTicketItems.loadingItemsStatus
  );
  const landingUrl = process.env.REACT_APP_LANDING_URL;
  const storageUrl = process.env.REACT_APP_STORAGE_URL;

  const apiRequest = useApiRequest();
  const [longUrl, setLongUrl] = useState("");
  const [viewingEvent, setViewingEvent] = useState<TypeEventsTicketItem | null>(
    null
  );
  // only one row menu open at a time
  const [openMenuId, setOpenMenuId] = useState<number | string | null>(null);

  const accessToken = useAppSelector(selectAccessToken);

  useEffect(() => {
    dispatch(resetCursor());
    dispatch(fetchEventsTicketItems());
  }, [ntumaAccount]);

  const copyLink = (value: string) => {
    dispatch(
      showToastMessage({
        message: "Copied",
        type: "success",
        position: toast.POSITION.TOP_RIGHT,
      })
    );
    navigator.clipboard.writeText(value);
  };

  /* ---------- "Aug 29, 2026" or "Aug 29 – Sep 04, 2026" ---------- */
  const dateRange = (item: TypeEventsTicketItem): string => {
    if (!item.event_date) return "—";

    const start = formatDate(item.event_date, "MMM DD, YYYY");

    // single day events store the same value in both, and older rows
    // have no end date at all
    if (!item.end_date) return start;

    const startDay = String(item.event_date).split("T")[0];
    const endDay = String(item.end_date).split("T")[0];
    if (startDay === endDay) return start;

    return (
      formatDate(item.event_date, "MMM DD") +
      " – " +
      formatDate(item.end_date, "MMM DD, YYYY")
    );
  };

  const isMultiDay = (item: TypeEventsTicketItem): boolean => {
    if (!item.event_date || !item.end_date) return false;
    return (
      String(item.event_date).split("T")[0] !==
      String(item.end_date).split("T")[0]
    );
  };

  /* ---------- price range across ticket types ---------- */
  const priceRange = (item: TypeEventsTicketItem): string => {
    const types = item.ticket_types || [];
    if (types.length === 0) return "—";

    const prices = types.map((t) => Number(t.price) || 0);
    const min = Math.min(...prices);
    const max = Math.max(...prices);

    if (min === 0 && max === 0) return "Free";
    if (min === max) return min.toLocaleString();
    return min.toLocaleString() + " – " + max.toLocaleString();
  };

  /* ---------- tickets sold / available ---------- */
  const soldSummary = (item: TypeEventsTicketItem): string => {
    const types = item.ticket_types || [];
    if (types.length === 0) return "—";

    const sold = types.reduce((total, t) => total + (Number(t.sold) || 0), 0);
    const capacity = types.reduce(
      (total, t) => total + (Number(t.capacity) || 0),
      0
    );

    // capacity 0 on a type means unlimited, so only show a denominator
    // when every type is capped
    const allCapped = types.every((t) => Number(t.capacity) > 0);

    if (allCapped && capacity > 0) {
      return sold.toLocaleString() + " / " + capacity.toLocaleString();
    }
    return sold.toLocaleString();
  };

  useEffect(() => {
    if (longUrl) {
      const fetchShortenedUrl = async () => {
        const apiUrl = SHORTEN_URL.API_HOST;
        const toastLoader = toast.loading("Processing");

        try {
          const response = await apiRequest({
            method: "post",
            url: apiUrl + "api/shorten_url",
            headers: { "Content-Type": "application/json" },
            data: {
              long_url: longUrl,
            },
          });

          toast.dismiss(toastLoader);

          if (response.status === "SUCCESS") {
            const getShortenedUrl = response.short_url;
            copyLink(getShortenedUrl);
          } else {
            dispatch(
              showToastMessage({
                message: response.message,
                type: "error",
                position: toast.POSITION.TOP_RIGHT,
              })
            );
          }
        } catch (error) {
          dispatch(
            showToastMessage({
              message: "Failed to process shortened URL.",
              type: "error",
              position: toast.POSITION.TOP_RIGHT,
            })
          );
        } finally {
          toast.dismiss(toastLoader);
          setLongUrl("");
        }
      };

      fetchShortenedUrl();
    }
  }, [longUrl]);

  /* ---------- hide (soft delete) ---------- */
  const hideEvent = async (id: number | string) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this event?"
    );
    if (!confirmDelete) return;

    const hideUrl = BASE_URL + "/api/donation-link/hide_event_ticket/" + id;
    const toastLoader = toast.loading("Processing");

    try {
      await apiRequest({
        method: "post",
        url: hideUrl,
        headers: {
          Authorization: "Bearer " + accessToken,
        },
        data: {
          shouldHide: true,
        },
      });

      dispatch(
        showToastMessage({
          message: "Event deleted!",
          type: "success",
          position: toast.POSITION.TOP_RIGHT,
        })
      );

      // drop it locally rather than refetching the whole list, which
      // would reset the cursor and lose any "load more" pages
      dispatch(removeEventsTicketItem(id));
    } catch (error) {
      const errorResponse = error as AxiosError;
      if (errorResponse.response) {
        const data = errorResponse.response.data as {
          message: string;
          status: string;
        };
        toast.error(data.message, {
          position: toast.POSITION.TOP_RIGHT,
        });
      } else {
        toast.error("Something went wrong", {
          position: toast.POSITION.TOP_RIGHT,
        });
      }
    } finally {
      toast.dismiss(toastLoader);
    }
  };

  return (
    <div>
      <div className="card mb-12">
        <div className="card-body flex flex-col px-4 py-4 min-h-half-screen pb-16">
          <div className="flex">
            <nav className="flex justify-between " aria-label="Breadcrumb">
              <ol className="inline-flex items-center mb-3 sm:mb-0">
                <li>
                  <div className="flex items-center">
                    <h5 className=" card-title ">Events Tickets</h5>
                  </div>
                </li>
              </ol>
            </nav>
            <div className="flex-grow flex justify-end">
              <button
                onClick={() => navigate("/new-events-ticket-item")}
                type="button"
                className="prim-btn-1 mb-2"
              >
                New Event
              </button>
            </div>
          </div>

          {eventsTicketItems.length === 0 &&
            loadingLinksStatus !== APP_STATUS.PENDING && (
              <div className="h-fit flex-grow flex flex-col justify-center">
                <div className="flex justify-center">
                  <div>
                    <img className="w-32" src={NoData} alt="" />
                    <p>No Events Yet!</p>
                  </div>
                </div>
              </div>
            )}

          {eventsTicketItems.length > 0 && (
            <table className="w-full text-sm text-left text-gray-500 dark:text-gray-400">
              <thead>
                <tr>
                  <th scope="col">Event</th>
                  <th scope="col">Type</th>
                  <th scope="col">Category</th>
                  <th scope="col">Venue</th>
                  <th scope="col">Date</th>
                  <th scope="col">Tickets</th>
                  <th scope="col">Price (UGX)</th>
                  <th scope="col">Sold</th>
                  <th scope="col" className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {eventsTicketItems.map((item, key) => (
                  <tr key={key}>
                    <td className="max-w-[260px]">
                      <div className="flex items-center gap-3 py-1">
                        {/* the banner makes a long list scannable at a glance */}
                        <div
                          className="h-11 w-16 rounded-md bg-gray-100 shrink-0 border border-gray-200"
                          style={{
                            backgroundImage: item.image
                              ? `url(${storageUrl}/${item.image})`
                              : "none",
                            backgroundSize: "cover",
                            backgroundPosition: "center",
                            backgroundRepeat: "no-repeat",
                          }}
                        ></div>
                        <div className="min-w-0">
                          <div className="text-gray-800 font-medium truncate">
                            {item.title}
                          </div>
                          {item.tagline && (
                            <div className="text-xs text-gray-400 truncate">
                              {item.tagline}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td>
                      {item.type
                        ? item.type.charAt(0).toUpperCase() + item.type.slice(1)
                        : "—"}
                    </td>
                    <td>{item.category || "—"}</td>
                    <td className="max-w-[180px]">{item.venue || "—"}</td>
                    <td>
                      {dateRange(item)}
                      {item.start_time && (
                        <div className="text-xs text-gray-400">
                          {item.start_time}
                          {item.end_time ? " – " + item.end_time : " till late"}
                          {isMultiDay(item) ? " daily" : ""}
                        </div>
                      )}
                    </td>
                    <td>
                      {item.ticket_types && item.ticket_types.length > 0
                        ? item.ticket_types.map((t) => t.name).join(", ")
                        : "—"}
                    </td>
                    <td>{priceRange(item)}</td>
                    <td>{soldSummary(item)}</td>

                    <td className="text-right whitespace-nowrap">
                      <div className="inline-flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => setViewingEvent(item)}
                          title="Sales, transactions and ticket breakdown"
                          className="prim-btn-1 whitespace-nowrap text-xs px-3 py-1.5"
                        >
                          Event details
                        </button>

                        {/* everything else folds into one menu so the row
                            does not sprawl across five columns */}
                        <div className="relative">
                          <button
                            type="button"
                            title="More actions"
                            onClick={() =>
                              setOpenMenuId(
                                openMenuId === item.id ? null : item.id
                              )
                            }
                            className="h-8 w-8 rounded-full hover:bg-gray-100 text-gray-500 flex items-center justify-center"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                            >
                              <path
                                fill="currentColor"
                                d="M12 8a2 2 0 1 1 0-4a2 2 0 0 1 0 4Zm0 6a2 2 0 1 1 0-4a2 2 0 0 1 0 4Zm0 6a2 2 0 1 1 0-4a2 2 0 0 1 0 4Z"
                              />
                            </svg>
                          </button>

                          {openMenuId === item.id && (
                            <>
                              {/* click anywhere else to dismiss */}
                              <div
                                className="fixed inset-0 z-10"
                                onClick={() => setOpenMenuId(null)}
                              ></div>

                              <div className="absolute right-0 mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-20 py-1 text-left">
                                <Link
                                  to={`/events-tickets/edit/${item.ref}`}
                                  className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                                >
                                  Edit event
                                </Link>

                                <button
                                  type="button"
                                  onClick={() => {
                                    copyLink(landingUrl + "/ticket/" + item.ref);
                                    setOpenMenuId(null);
                                  }}
                                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                                >
                                  Copy link
                                </button>

                                <button
                                  type="button"
                                  onClick={() => {
                                    setLongUrl(
                                      landingUrl + "/ticket/" + item.ref
                                    );
                                    setOpenMenuId(null);
                                  }}
                                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                                >
                                  Copy short link
                                </button>

                                <a
                                  href={landingUrl + "/ticket/" + item.ref}
                                  target="_blank"
                                  rel="noreferrer"
                                  onClick={() => setOpenMenuId(null)}
                                  className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                                >
                                  Open public page
                                </a>

                                <div className="border-t my-1"></div>

                                <button
                                  type="button"
                                  onClick={() => {
                                    setOpenMenuId(null);
                                    hideEvent(item.id);
                                  }}
                                  className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-50"
                                >
                                  Delete event
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <EventDetailsModal
            isOpen={viewingEvent !== null}
            onClose={() => setViewingEvent(null)}
            event={viewingEvent}
          />

          {loadingLinksStatus === APP_STATUS.PENDING && <AppSpinner />}
          {hasMore && loadingLinksStatus !== APP_STATUS.PENDING && (
            <div className=" flex justify-center mt-16">
              <button
                onClick={() => {
                  dispatch(fetchEventsTicketItems());
                }}
                type="button"
                className="btn-light text-gray-600 "
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  className=" text-gray-600"
                  viewBox="0 0 24 24"
                >
                  <path
                    fill="none"
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M14 16h5v5M10 8H5V3m14.418 6.003A8 8 0 0 0 5.086 7.976m-.504 7.021a8 8 0 0 0 14.331 1.027"
                  />
                </svg>
                <span className="pl-1">Load More</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
export default EventsTicketLinksPage;
